import { JournalLine, JournalManifest } from '../types/journal';

/** cyrb53, a fast 53-bit string hash. Not Web Crypto, which the desktop ES5 runtime does not have. */
function hash53(text: string): string {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

/** What a send would carry: the lines as read from the sheet, and the journal's name. */
export function journalFingerprint(lines: JournalLine[], journalName: string): string {
  return hash53(JSON.stringify({ journalName, lines }));
}

/**
 * A send carries the version the last passing check made, and only while the sheet still reads the
 * same; anything else is checked again first, so the ERP never receives lines the sheet no longer shows.
 */
export function sendNeedsCheck(manifest: JournalManifest, fingerprint: string): boolean {
  return !manifest.versionId || manifest.checkedFingerprint !== fingerprint;
}

/** A description cell can hold line breaks and stray spaces; the ERP's description is one line. */
export function normalizeDescription(raw: unknown): string {
  return raw === null || raw === undefined ? '' : String(raw).replace(/\s+/g, ' ').trim();
}

/** The name a journal gets without a description: dated by its sheet, so it does not change from day to day. */
export function defaultJournalName(manifest: Pick<JournalManifest, 'createdAt'>, now: Date = new Date()): string {
  const created = /^\d{4}-\d{2}-\d{2}/.test(manifest.createdAt ?? '') ? manifest.createdAt.slice(0, 10) : now.toISOString().slice(0, 10);
  return `Journal ${created}`;
}

export function journalNameFor(description: string, manifest: Pick<JournalManifest, 'createdAt'>): string {
  return description || defaultJournalName(manifest);
}
